import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { PageBody } from "../components/Layout";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowsClockwise, Quotes, Warning, Sparkle, Flask,
} from "@phosphor-icons/react";

const STATUS_TONES = {
    draft:        { label: "Draft",        cls: "bg-muted text-muted-foreground" },
    validated:    { label: "Validated",    cls: "bg-foreground text-background" },
    challenged:   { label: "Challenged",   cls: "bg-accent/15 text-accent" },
    inconclusive: { label: "Inconclusive", cls: "bg-muted text-foreground/70" },
};

const ALIGN_TONES = {
    strong: "text-foreground",
    mixed: "text-foreground/70",
    low: "text-muted-foreground",
};

const MOMENTUM_TONES = {
    rising: "text-accent",
    flat: "text-foreground/70",
    cooling: "text-muted-foreground",
    unknown: "text-muted-foreground",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", {
            year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return ""; }
}

import NarrativeStrengthMeter, { getStrengthBand } from "../components/NarrativeStrengthMeter";

function StrengthDisplay({ value, alignment, momentum }) {
    const v = Math.max(0, Math.min(100, Math.round(value || 0)));
    const band = getStrengthBand(v);
    return (
        <div data-testid="strength-display" className="flex items-end gap-7 flex-wrap">
            <div className="flex flex-col">
                <span className="overline text-[10px] mb-1.5">Narrative strength</span>
                <div className="flex items-baseline gap-2">
                    <span className="font-display font-bold tabular-nums text-5xl leading-none">{v}</span>
                    <span className="font-mono text-[11px] text-muted-foreground tracking-wider">/ 100</span>
                </div>
                <div className="mt-3 h-1 w-44 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${band.barCls}`} style={{ width: `${v}%` }} />
                </div>
                <div className="mt-3">
                    <NarrativeStrengthMeter value={v} compact showLabel={true} />
                </div>
            </div>
            <div className="flex flex-col">
                <span className="overline text-[10px] mb-1.5">Cultural alignment</span>
                <span className={`font-display font-semibold text-xl ${ALIGN_TONES[alignment] || ALIGN_TONES.low}`}>
                    {alignment ? alignment.charAt(0).toUpperCase() + alignment.slice(1) : "—"}
                </span>
            </div>
            <div className="flex flex-col">
                <span className="overline text-[10px] mb-1.5">Trend momentum</span>
                <span className={`font-display font-semibold text-xl ${MOMENTUM_TONES[momentum] || MOMENTUM_TONES.unknown}`}>
                    {momentum ? momentum.charAt(0).toUpperCase() + momentum.slice(1) : "—"}
                </span>
            </div>
        </div>
    );
}

function QuoteList({ quotes, kind }) {
    if (!quotes?.length) {
        return (
            <p className="font-mono text-[11px] text-muted-foreground italic">
                {kind === "supporting"
                    ? "No supporting quotes returned for this run."
                    : "No contradictory signals surfaced — that itself is a finding worth interrogating."}
            </p>
        );
    }
    const accent = kind === "contradictory" ? "border-accent" : "border-foreground/40";
    return (
        <ul className="space-y-5">
            {quotes.map((q, i) => (
                <li key={i} data-testid={`${kind}-quote-${i}`}
                    className={`pl-5 border-l-2 ${accent}`}>
                    <Quotes size={13} className="text-accent mb-1" />
                    <p className="font-display italic text-foreground/90 text-[15px] leading-snug">
                        "{q.quote}"
                    </p>
                    <p className="font-mono text-[10px] text-muted-foreground mt-1.5 tracking-wider">
                        — {q.platform || "—"}
                        {q.like_count > 0 && <> · {q.like_count.toLocaleString()} likes</>}
                        {q.stream_name && <> · {q.stream_name}</>}
                    </p>
                    {q.why && (
                        <p className="text-[12px] text-muted-foreground mt-2 leading-relaxed">
                            <span className="overline text-[9px] mr-1.5">why</span>{q.why}
                        </p>
                    )}
                </li>
            ))}
        </ul>
    );
}

export default function HypothesisDetail() {
    const { id } = useParams();
    const [h, setH] = useState(null);
    const [run, setRun] = useState(null);
    const [running, setRunning] = useState(false);

    const load = async () => {
        const [hr, rr] = await Promise.all([
            api.get(`/labs/hypotheses/${id}`).then((r) => r.data),
            api.get(`/labs/hypotheses/${id}/latest-run`).then((r) => r.data).catch(() => null),
        ]);
        setH(hr);
        setRun(rr);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const runValidation = async () => {
        setRunning(true);
        try {
            const r = await api.post(`/labs/hypotheses/${id}/run`, {});
            setRun(r.data);
            toast.success("Hypothesis pressure-tested");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Validation failed");
        } finally {
            setRunning(false);
        }
    };

    if (!h) return <PageBody><div className="text-sm text-muted-foreground">Loading…</div></PageBody>;

    const tone = STATUS_TONES[h.status] || STATUS_TONES.draft;

    return (
        <>
            <header data-testid="hypothesis-header" className="px-8 lg:px-14 pt-10 pb-7 border-b border-border bg-surface/40">
                <div className="max-w-[1100px] mx-auto">
                    <Link to="/labs" data-testid="back-to-labs" className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-wider text-muted-foreground hover:text-foreground mb-5">
                        <ArrowLeft size={12} /> Narrative Labs
                    </Link>
                    <div className="flex items-center gap-3 mb-4">
                        <span className={`overline text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-sm ${tone.cls}`}>
                            {tone.label}
                        </span>
                        {run && (
                            <span className="font-mono text-[10px] text-muted-foreground tracking-wider">
                                {run.candidate_count} candidates · {run.platforms?.join(" · ") || "—"} · last run {fmtDate(run.created_at)}
                            </span>
                        )}
                    </div>
                    <h1 data-testid="hypothesis-statement"
                        className="font-display font-bold text-[clamp(1.4rem,2.6vw,2.4rem)] leading-tight tracking-tight max-w-[900px]">
                        "{h.statement}"
                    </h1>
                    {h.notes && (
                        <p className="text-sm text-muted-foreground mt-3 max-w-[700px]">{h.notes}</p>
                    )}
                    <div className="mt-6 flex gap-2 flex-wrap">
                        <Button
                            data-testid="run-validation-btn"
                            onClick={runValidation}
                            disabled={running}
                            className="rounded-sm"
                        >
                            {running
                                ? <ArrowsClockwise size={13} className="animate-spin mr-1.5" />
                                : <Flask size={13} weight="duotone" className="mr-1.5" />}
                            {run ? "Re-test against signals" : "Pressure-test now"}
                        </Button>
                    </div>
                </div>
            </header>

            <PageBody>
                <article className="max-w-[1100px] mx-auto">
                    {!run ? (
                        <div data-testid="hypothesis-empty"
                             className="border border-dashed border-border rounded-sm p-12 text-center bg-card">
                            <Flask size={28} weight="duotone" className="mx-auto text-foreground/40 mb-3" />
                            <h3 className="font-display font-bold text-xl mb-1.5">Not yet pressure-tested.</h3>
                            <p className="text-sm text-muted-foreground max-w-[480px] mx-auto">
                                Click <strong>Pressure-test now</strong> to validate this hypothesis against your collected signal pool. The system will surface supporting evidence, contradictory signals, and the assumptions inside the statement that the data does <em>not</em> yet support.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Strength + alignment + momentum strip */}
                            <section data-testid="strength-strip" className="border border-border bg-card p-7 mb-12">
                                <StrengthDisplay
                                    value={run.narrative_strength}
                                    alignment={run.cultural_alignment}
                                    momentum={run.trend_momentum}
                                />
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-7 pt-6 border-t border-border">
                                    <div>
                                        <div className="overline text-[10px] mb-1">Supporting</div>
                                        <div className="font-mono tabular-nums text-2xl text-foreground">{run.supporting_count}</div>
                                    </div>
                                    <div>
                                        <div className="overline text-[10px] mb-1">Contradictory</div>
                                        <div className="font-mono tabular-nums text-2xl text-accent">{run.contradictory_count}</div>
                                    </div>
                                    <div>
                                        <div className="overline text-[10px] mb-1">Candidates</div>
                                        <div className="font-mono tabular-nums text-2xl text-foreground/80">{run.candidate_count}</div>
                                    </div>
                                    <div>
                                        <div className="overline text-[10px] mb-1">Resonance</div>
                                        <div className="font-mono tabular-nums text-2xl text-foreground/80">{(run.resonance_total || 0).toLocaleString()}</div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-x-7 gap-y-2 mt-5 text-[11px] font-mono text-muted-foreground">
                                    {run.platforms?.length > 0 && (
                                        <span><span className="overline text-[9px] mr-1.5">platforms</span>{run.platforms.join(" · ")}</span>
                                    )}
                                    {run.streams?.length > 0 && (
                                        <span><span className="overline text-[9px] mr-1.5">streams</span>{run.streams.slice(0, 3).join(", ")}{run.streams.length > 3 && ` +${run.streams.length - 3}`}</span>
                                    )}
                                </div>
                            </section>

                            {/* Strategic interpretation — the headline editorial read */}
                            {run.strategic_interpretation && (
                                <section data-testid="strategic-interpretation" className="mb-14">
                                    <div className="flex items-baseline gap-3 mb-4">
                                        <div className="overline">Strategic interpretation</div>
                                        <div className="h-px bg-border flex-1" />
                                    </div>
                                    <p className="editorial-lead text-[18px] md:text-[20px] leading-relaxed text-foreground/90 max-w-[68ch]">
                                        {run.strategic_interpretation}
                                    </p>
                                </section>
                            )}

                            {/* WHAT ASSUMPTIONS MIGHT BE WEAK — the editorial conscience */}
                            {run.weak_assumptions?.length > 0 && (
                                <section data-testid="weak-assumptions" className="mb-14 border-l-2 border-accent pl-6 py-2">
                                    <div className="flex items-baseline gap-3 mb-4">
                                        <Warning size={14} className="text-accent" />
                                        <div className="overline text-accent">What assumptions might be weak</div>
                                        <div className="h-px bg-border flex-1" />
                                    </div>
                                    <ul className="space-y-3">
                                        {run.weak_assumptions.map((a, i) => (
                                            <li key={i} data-testid={`weak-assumption-${i}`}
                                                className="font-display text-[16px] leading-snug text-foreground/90 max-w-[68ch]">
                                                <span className="font-mono text-[10px] text-muted-foreground mr-2 tracking-wider">{String(i + 1).padStart(2, "0")} ·</span>
                                                {a}
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {/* Counter-signals */}
                            {run.counter_signals?.length > 0 && (
                                <section data-testid="counter-signals" className="mb-14">
                                    <div className="flex items-baseline gap-3 mb-4">
                                        <div className="overline">Counter-signals</div>
                                        <div className="h-px bg-border flex-1" />
                                        <span className="font-mono text-[11px] text-muted-foreground">cultural patterns that complicate the hypothesis</span>
                                    </div>
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                                        {run.counter_signals.map((s, i) => (
                                            <li key={i} className="text-[15px] leading-snug text-foreground/85 border-l border-border pl-4">
                                                {s}
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {/* Recurring language */}
                            {run.recurring_language?.length > 0 && (
                                <section data-testid="recurring-language" className="mb-14">
                                    <div className="flex items-baseline gap-3 mb-4">
                                        <div className="overline">Recurring language</div>
                                        <div className="h-px bg-border flex-1" />
                                    </div>
                                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-base text-foreground/85">
                                        {run.recurring_language.map((p, i) => (
                                            <span key={i} className="font-display italic">"{p}"</span>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Quotes — supporting + contradictory side-by-side */}
                            <section className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10 mb-14">
                                <div>
                                    <div className="flex items-baseline gap-3 mb-5">
                                        <div className="overline">Supporting evidence</div>
                                        <span className="font-mono text-[10px] text-muted-foreground tabular-nums">{run.supporting_quotes?.length || 0}</span>
                                    </div>
                                    <QuoteList quotes={run.supporting_quotes} kind="supporting" />
                                </div>
                                <div>
                                    <div className="flex items-baseline gap-3 mb-5">
                                        <div className="overline text-accent">Contradictory evidence</div>
                                        <span className="font-mono text-[10px] text-accent tabular-nums">{run.contradictory_quotes?.length || 0}</span>
                                    </div>
                                    <QuoteList quotes={run.contradictory_quotes} kind="contradictory" />
                                </div>
                            </section>

                            <div className="font-mono text-[10px] text-muted-foreground tracking-wider text-right">
                                synthesized · {(run.model_used || "").split(":")[0] || "openai"}
                            </div>
                        </>
                    )}
                </article>
            </PageBody>
        </>
    );
}
